import {
  AsYouType,
  parsePhoneNumberWithError,
  type CountryCode,
} from 'libphonenumber-js';
import React, {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { StyleSheet, type TextInput as RNTextInput } from 'react-native';
import type { TextInputProps as PaperTextInputProps } from 'react-native-paper';
import { TextInput } from './TextInput';

type Props = {
  label: string;
  showLabel?: boolean;
  value: string;
  onChange?: (value: string) => void;
  icon?: ReactNode;
  onIconPress?: () => void;
  error?: boolean;
  validateOnChange?: boolean;
  defaultCountry?: string; // ISO 3166-1 alpha-2 code (e.g., 'US', 'CA', 'GB')
} & Omit<
  PaperTextInputProps,
  'onChangeText' | 'onChange' | 'value' | 'keyboardType' | 'autoComplete'
>;

export const PhoneInput = forwardRef<RNTextInput, Props>(
  (
    {
      label,
      showLabel = true,
      value,
      onChange,
      icon,
      style,
      disabled,
      onIconPress,
      error,
      validateOnChange = false,
      defaultCountry = 'US',
      ...otherProps
    }: Props,
    ref,
  ) => {
    const [formattedValue, setFormattedValue] = useState('');

    // Synchronize formatted value when value prop changes externally
    useEffect(() => {
      if (value) {
        const formatter = new AsYouType(defaultCountry as CountryCode);
        const formatted = formatter.input(value);
        setFormattedValue(formatted);
      } else {
        setFormattedValue('');
      }
    }, [value, defaultCountry]);

    // Validate phone number
    const isValidPhone = (phone: string): boolean => {
      if (!phone) return true; // Empty is valid (let required prop handle it)
      try {
        const phoneNumber = parsePhoneNumberWithError(
          phone,
          defaultCountry as CountryCode,
        );
        return phoneNumber?.isValid() ?? false;
      } catch {
        return false;
      }
    };

    const handleChange = (text: string) => {
      if (!onChange) return;

      // Format as you type
      const formatter = new AsYouType(defaultCountry as CountryCode);
      const formatted = formatter.input(text);

      setFormattedValue(formatted);
      onChange(text);
    };

    // Show error if:
    // 1. error prop is explicitly set, OR
    // 2. validateOnChange is true AND value is not empty AND phone is invalid
    const hasError =
      error || (validateOnChange && value.length > 0 && !isValidPhone(value));

    // Memoize error style to avoid recreation
    const errorStyle = useMemo(
      () => (hasError ? styles.errorInput : undefined),
      [hasError],
    );

    // Use formatted value if available, otherwise use raw value
    const displayValue = formattedValue || value;

    return (
      <TextInput
        ref={ref}
        label={label}
        showLabel={showLabel}
        value={displayValue}
        onChange={handleChange}
        icon={icon}
        onIconPress={onIconPress}
        disabled={disabled}
        keyboardType="phone-pad"
        autoComplete="tel"
        textContentType="telephoneNumber"
        maxLength={20}
        numberOfLines={1}
        style={[errorStyle, style]}
        {...otherProps}
      />
    );
  },
);

PhoneInput.displayName = 'PhoneInput';

const styles = StyleSheet.create({
  errorInput: {
    // borderColor: Colors.pink0,
    // borderWidth: 1,
  },
});
